import {
  integrationCustomFieldMappingSchema,
  integrationSchema,
  integrationSyncErrorResponseSchema,
  oAuthCredentialStatusResponseSchema,
  pendingItemsCountResponseSchema,
} from '@tempo-io/work-integration-dto';
import { HTTPError } from 'ky';
import { z } from 'zod';
import { tempoApi } from '@/common/api/tempoApi';
import {
  type ExternalWorkIntegrationStatusValues,
  type IntegrationCustomFieldMapping,
  type OAuthCredentialStatusResponse,
  type PendingItemsCountResponse,
  type SyncDirection,
  SyncType,
  type WorkIntegrationFilters,
  type WorkIntegrationResponse,
  type WorkIntegrationSyncRequest,
} from '@/common/api/types/workIntegrations';
import type { AdoImportWizardSubmissionSchema } from '@/features/settings/work-integrations/components/work-integration-dialog/ado/lib/types';
import type { JiraImportWizardSubmissionSchema } from '@/features/settings/work-integrations/components/work-integration-dialog/jira/lib/types';

export type SyncWorkIntegrationResult =
  | { type: 'queued'; workIntegration: WorkIntegrationResponse }
  | { type: 'no-items' }
  | { type: 'no-field-mappings' };

const getAllWorkIntegrations = async (options?: {
  limit?: number;
  filter?: {
    statuses?: ExternalWorkIntegrationStatusValues[];
    ids?: number[];
    loopIds?: number[];
  };
}) => {
  const searchParams = new URLSearchParams();

  if (options?.limit) {
    searchParams.append('limit', options.limit.toString());
  }

  if (options?.filter?.statuses?.length) {
    options.filter.statuses.forEach((status) => {
      searchParams.append('statuses', status);
    });
  }

  if (options?.filter?.ids?.length) {
    searchParams.append('ids', options.filter.ids.join(','));
  }

  if (options?.filter?.loopIds?.length) {
    searchParams.append('loopIds', options.filter.loopIds.join(','));
  }

  const url = `api/v1/work-integrations${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  const response = await tempoApi().get(url).json();
  return z.array(integrationSchema).parse(response);
};

const buildSyncScope = (
  payload: WorkIntegrationSyncRequest,
): { sourceItemIds?: string[]; itemIds?: readonly number[] } | undefined => {
  const scope: { sourceItemIds?: string[]; itemIds?: readonly number[] } = {};

  if (payload.sourceItemIds?.length) scope.sourceItemIds = payload.sourceItemIds;
  if (payload.itemIds?.length) scope.itemIds = payload.itemIds;

  return Object.keys(scope).length > 0 ? scope : undefined;
};

const syncWorkIntegration = async ({
  workIntegrationId,
  payload,
}: {
  workIntegrationId: number;
  payload: WorkIntegrationSyncRequest;
}): Promise<SyncWorkIntegrationResult> => {
  const endpoint = payload.syncType === SyncType.PULL ? 'pull' : 'push';
  const scope = buildSyncScope(payload);

  try {
    const response = await tempoApi().post(
      `api/v1/work-integrations/${workIntegrationId}/${endpoint}`,
      scope && { json: scope },
    );

    if (response.status === 204) {
      return { type: 'no-items' };
    }

    return { type: 'queued', workIntegration: integrationSchema.parse(await response.json()) };
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 400) {
      const errorData = await error.response.json<{ message: string }>();
      if (errorData.message === 'NO_FIELD_MAPPINGS') {
        return { type: 'no-field-mappings' };
      }
    }
    throw error;
  }
};

const deleteWorkIntegration = async ({ workIntegrationId }: { workIntegrationId: number }) => {
  return tempoApi().delete(`api/v1/work-integrations/${workIntegrationId}`);
};

const submitWorkIntegration = async ({
  payload,
}: {
  payload: JiraImportWizardSubmissionSchema | AdoImportWizardSubmissionSchema;
}) => {
  const response = await tempoApi()
    .post('api/v1/work-integrations', {
      json: payload,
    })
    .json();
  return integrationSchema.parse(response);
};

const getWorkIntegrationErrors = async ({ workIntegrationId }: { workIntegrationId: number }) => {
  const response = await tempoApi()
    .get(`api/v1/work-integrations/${workIntegrationId}/errors`)
    .json();
  return integrationSyncErrorResponseSchema.parse(response);
};

const updateWorkIntegrationCredentials = async ({
  workIntegrationId,
  oauthCredentialsId,
}: {
  workIntegrationId: number;
  oauthCredentialsId: number;
}): Promise<WorkIntegrationResponse> => {
  const response = await tempoApi()
    .patch(
      `api/v1/work-integrations/${workIntegrationId}/credentials?oauthCredentialsId=${oauthCredentialsId}`,
    )
    .json();
  return integrationSchema.parse(response);
};

const updateWorkIntegrationFilters = async ({
  integrationId,
  filters,
}: {
  integrationId: number;
  filters: WorkIntegrationFilters;
}): Promise<WorkIntegrationResponse> => {
  const response = await tempoApi()
    .patch(`api/v1/work-integrations/${integrationId}/filters`, { json: filters })
    .json();
  return integrationSchema.parse(response);
};

const toggleSynchronizationStatus = async ({
  workIntegrationId,
}: {
  workIntegrationId: number;
}): Promise<WorkIntegrationResponse> => {
  const response = await tempoApi()
    .patch(`api/v1/work-integrations/${workIntegrationId}/synchronization-status`)
    .json();
  return integrationSchema.parse(response);
};

const updatePushRankSyncEnabled = async ({
  workIntegrationId,
  rankSyncEnabled,
}: {
  workIntegrationId: number;
  rankSyncEnabled: boolean;
}): Promise<WorkIntegrationResponse> => {
  const response = await tempoApi()
    .patch(
      `api/v1/work-integrations/${workIntegrationId}/rank-sync?rankSyncEnabled=${rankSyncEnabled}`,
    )
    .json();
  return integrationSchema.parse(response);
};

const updateFieldMappings = async ({
  integrationId,
  standardFieldsMappings,
  customFieldMappings,
}: {
  integrationId: number;
  standardFieldsMappings: {
    estimateFieldId?: string | null;
    estimationSchemeId?: number | null;
    teamFieldId?: string | null;
    expectedStartDateFieldId?: string | null;
    expectedFinishDateFieldId?: string | null;
    plannedStartDateFieldId?: string | null;
    plannedFinishDateFieldId?: string | null;
  };
  customFieldMappings: { instanceFieldMappingId: number; syncDirection: SyncDirection }[];
}): Promise<void> => {
  await tempoApi().put(`api/v1/work-integrations/${integrationId}/field-mappings`, {
    json: { standardFieldsMappings, customFieldMappings },
  });
};

const getIntegrationCustomFieldMappings = async ({
  integrationId,
}: {
  integrationId: number;
}): Promise<IntegrationCustomFieldMapping[]> => {
  const response = await tempoApi()
    .get(`api/v1/work-integrations/${integrationId}/custom-field-mappings`)
    .json();
  return z.array(integrationCustomFieldMappingSchema).parse(response);
};

const getPendingItemsCount = async ({
  integrationId,
}: {
  integrationId: number;
}): Promise<PendingItemsCountResponse> => {
  const response = await tempoApi()
    .get(`api/v1/work-integration/integrations/${integrationId}/pending-count`)
    .json();
  return pendingItemsCountResponseSchema.parse(response);
};

const getOAuthCredentialStatuses = async (
  credentialIds: number[],
): Promise<OAuthCredentialStatusResponse> => {
  if (credentialIds.length === 0) {
    return { statuses: [] };
  }

  const searchParams = new URLSearchParams();
  credentialIds.forEach((credentialId) => {
    searchParams.append('ids', credentialId.toString());
  });

  const response = await tempoApi()
    .get(`api/v1/work-integration/oauth-credentials/status?${searchParams.toString()}`)
    .json();

  return oAuthCredentialStatusResponseSchema.parse(response);
};

export const WorkIntegrationsApi = {
  submitWorkIntegration,
  getAllWorkIntegrations,
  syncWorkIntegration,
  deleteWorkIntegration,
  getWorkIntegrationErrors,
  updateWorkIntegrationCredentials,
  updateWorkIntegrationFilters,
  updateFieldMappings,
  toggleSynchronizationStatus,
  updatePushRankSyncEnabled,
  getOAuthCredentialStatuses,
  getIntegrationCustomFieldMappings,
  getPendingItemsCount,
};
